import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell, PieChart, Pie, Legend
} from "recharts";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface CustomerData {
  id: string;
  name: string;
  totalSpent: number;
  purchaseCount: number;
  firstPurchase: string;
  lastPurchase: string;
  daysActive: number;
  purpose: string;
}

const PURPOSE_LABELS: Record<string, string> = {
  hobby: "趣味", skill: "手に職", career: "仕事目的",
  return: "復職", change: "転職", business: "独立・副業",
  succession: "事業承継", research: "情報収集", other: "その他", unknown: "未設定",
};
const COLORS = ["#2D6A4F", "#C9A84C", "#40916C", "#D4A017", "#52B788", "#B7941D", "#1B4332", "#E9C46A"];

function formatYen(v: number) {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString()}`;
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: { name: string; value: number }[] }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone-200 rounded-xl px-3 py-2 text-xs font-sans">
      {payload.map((p) => (
        <p key={p.name} className="text-charcoal">
          {p.name}: {p.name.includes("¥") || p.name === "LTV" ? `¥${p.value.toLocaleString()}` : p.value}
        </p>
      ))}
    </div>
  );
}

export default function ProviderLTV() {
  const { currentUser } = useApp();
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewTab, setViewTab] = useState<"overview" | "customers" | "segments">("overview");

  useEffect(() => {
    if (!currentUser?.id) return;
    const load = async () => {
      setLoading(true);
      const { data: purchases } = await supabase
        .from("purchases")
        .select("buyer_id, price, purchased_at, product_title")
        .eq("provider_id", currentUser.id)
        .eq("status", "completed");

      if (!purchases || purchases.length === 0) { setLoading(false); return; }

      const buyerMap: Record<string, { prices: number[]; dates: string[] }> = {};
      purchases.forEach((p) => {
        if (!buyerMap[p.buyer_id]) buyerMap[p.buyer_id] = { prices: [], dates: [] };
        buyerMap[p.buyer_id].prices.push(p.price);
        buyerMap[p.buyer_id].dates.push(p.purchased_at);
      });

      const buyerIds = Object.keys(buyerMap);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, name, purpose")
        .in("id", buyerIds);

      const profileMap = Object.fromEntries((profiles || []).map((p) => [p.id, p]));

      const customerData: CustomerData[] = buyerIds.map((id) => {
        const { prices, dates } = buyerMap[id];
        const sorted = [...dates].sort();
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const days = Math.floor((new Date(last).getTime() - new Date(first).getTime()) / (1000 * 60 * 60 * 24));
        const profile = profileMap[id];
        return {
          id,
          name: profile?.name || "匿名ユーザー",
          totalSpent: prices.reduce((s, p) => s + p, 0),
          purchaseCount: prices.length,
          firstPurchase: first,
          lastPurchase: last,
          daysActive: days,
          purpose: profile?.purpose || "unknown",
        };
      });

      setCustomers(customerData.sort((a, b) => b.totalSpent - a.totalSpent));
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-3xl text-teal" /></div>;
  }

  const totalCustomers = customers.length;
  const totalRevenue = customers.reduce((s, c) => s + c.totalSpent, 0);
  const avgLTV = totalCustomers > 0 ? Math.round(totalRevenue / totalCustomers) : 0;
  const repeatCustomers = customers.filter((c) => c.purchaseCount > 1).length;
  const repeatRate = totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;
  const topCustomer = customers[0];

  // LTV distribution
  const ltvBuckets = [
    { label: "¥1〜¥2,999", min: 1, max: 2999 },
    { label: "¥3,000〜¥9,999", min: 3000, max: 9999 },
    { label: "¥10,000〜¥29,999", min: 10000, max: 29999 },
    { label: "¥30,000+", min: 30000, max: Infinity },
  ].map((b) => ({
    label: b.label,
    count: customers.filter((c) => c.totalSpent >= b.min && c.totalSpent <= b.max).length,
  }));

  // Purpose segments
  const purposeMap: Record<string, number> = {};
  customers.forEach((c) => {
    purposeMap[c.purpose] = (purposeMap[c.purpose] || 0) + c.totalSpent;
  });
  const purposeData = Object.entries(purposeMap)
    .map(([key, value]) => ({ name: PURPOSE_LABELS[key] || key, value }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      <div>
        <p className="font-display italic text-gold text-xs tracking-widest mb-1">Fan Analysis</p>
        <h2 className="font-serif text-deep-green text-2xl font-light">ファン分析・LTV追跡</h2>
        <p className="text-xs text-charcoal/50 font-sans mt-1">購入者の生涯顧客価値（LTV）と行動パターンを分析します</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "累計顧客数", value: `${totalCustomers}名`, icon: "ri-group-line", color: "text-teal" },
          { label: "平均LTV", value: avgLTV > 0 ? formatYen(avgLTV) : "—", icon: "ri-user-star-line", color: "text-gold" },
          { label: "リピート率", value: `${repeatRate.toFixed(1)}%`, icon: "ri-repeat-line", color: "text-deep-green" },
          { label: "最高LTV", value: topCustomer ? formatYen(topCustomer.totalSpent) : "—", icon: "ri-vip-crown-line", color: "text-gold" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-100">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className={`${s.icon} ${s.color} text-2xl`} />
            </div>
            <p className="font-serif text-2xl text-charcoal mt-2">{s.value}</p>
            <p className="text-xs text-charcoal/50 font-sans mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-full w-fit">
        {([
          { v: "overview", l: "概要" },
          { v: "customers", l: "顧客一覧" },
          { v: "segments", l: "セグメント分析" },
        ] as const).map((t) => (
          <button
            key={t.v}
            onClick={() => setViewTab(t.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-sans cursor-pointer transition-all whitespace-nowrap ${viewTab === t.v ? "bg-white text-deep-green font-medium" : "text-charcoal/50 hover:text-charcoal"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {totalCustomers === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-100 py-20 text-center">
          <i className="ri-group-line text-5xl text-charcoal/15 block mb-4" />
          <p className="font-serif text-charcoal/40 text-lg">まだ購入者がいません</p>
          <p className="text-sm text-charcoal/30 font-sans mt-2">最初の販売後、分析データが表示されます</p>
        </div>
      ) : viewTab === "overview" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LTV distribution */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="font-serif text-deep-green text-lg mb-5">LTV分布</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ltvBuckets} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f0ec" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#888", fontFamily: "Noto Sans JP" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip formatter={(v: number) => [`${v}名`, "顧客数"]} contentStyle={{ fontFamily: "Noto Sans JP", fontSize: "12px", borderRadius: "10px", border: "1px solid #e7e5e4" }} />
                <Bar dataKey="count" fill="#2D6A4F" radius={[4, 4, 0, 0]} maxBarSize={50} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Purchase frequency */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="font-serif text-deep-green text-lg mb-5">購入回数分布</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[
                  { label: "1回", count: customers.filter((c) => c.purchaseCount === 1).length },
                  { label: "2回", count: customers.filter((c) => c.purchaseCount === 2).length },
                  { label: "3回", count: customers.filter((c) => c.purchaseCount === 3).length },
                  { label: "4回+", count: customers.filter((c) => c.purchaseCount >= 4).length },
                ]}
                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f0ec" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#888", fontFamily: "Noto Sans JP" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip formatter={(v: number) => [`${v}名`, "顧客数"]} contentStyle={{ fontFamily: "Noto Sans JP", fontSize: "12px", borderRadius: "10px", border: "1px solid #e7e5e4" }} />
                <Bar dataKey="count" fill="#C9A84C" radius={[4, 4, 0, 0]} maxBarSize={50} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : viewTab === "customers" ? (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 bg-stone-50">
            <p className="text-xs text-charcoal/50 font-sans">LTV順に表示（上位{Math.min(customers.length, 20)}名）</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  {["順位", "顧客名", "LTV", "購入回数", "利用目的", "最終購入"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-sans text-charcoal/50 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {customers.slice(0, 20).map((c, i) => (
                  <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`text-sm font-sans font-bold ${i === 0 ? "text-gold" : i === 1 ? "text-stone-400" : i === 2 ? "text-amber-600" : "text-charcoal/30"}`}>
                        #{i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-sans text-charcoal font-medium">{c.name}</td>
                    <td className="px-4 py-3">
                      <span className="font-serif text-deep-green text-sm">{formatYen(c.totalSpent)}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-sans text-charcoal/60">{c.purchaseCount}回</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-deep-green/8 text-deep-green px-2 py-0.5 rounded-full font-sans whitespace-nowrap">
                        {PURPOSE_LABELS[c.purpose] || c.purpose}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-sans text-charcoal/50">
                      {new Date(c.lastPurchase).toLocaleDateString("ja-JP")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Purpose segments */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6">
            <h3 className="font-serif text-deep-green text-lg mb-5">利用目的別 売上貢献</h3>
            {purposeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={purposeData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                    {purposeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => [`¥${v.toLocaleString()}`, "売上"]} contentStyle={{ fontFamily: "Noto Sans JP", fontSize: "12px", borderRadius: "10px" }} />
                  <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: "11px", fontFamily: "Noto Sans JP", color: "#555" }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-charcoal/40 font-sans py-8 text-center">データなし</p>}
          </div>

          {/* Retention hint */}
          <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-4">
            <h3 className="font-serif text-deep-green text-lg">リテンション分析</h3>
            <div className="space-y-3">
              {[
                { label: "一度だけ購入", count: customers.filter(c => c.purchaseCount === 1).length, color: "bg-stone-300" },
                { label: "2回以上購入（リピーター）", count: customers.filter(c => c.purchaseCount >= 2).length, color: "bg-teal" },
                { label: "5回以上購入（ファン）", count: customers.filter(c => c.purchaseCount >= 5).length, color: "bg-gold" },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs font-sans text-charcoal/70">{item.label}</p>
                    <p className="text-xs font-sans font-medium text-charcoal">{item.count}名</p>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full transition-all duration-700`}
                      style={{ width: `${totalCustomers > 0 ? (item.count / totalCustomers) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-ivory rounded-xl p-4 border border-stone-100 mt-3">
              <p className="text-xs text-charcoal/60 font-sans leading-relaxed">
                <i className="ri-lightbulb-line text-gold mr-1" />
                <strong>ヒント：</strong>リピーター率を上げるには、購入後のメッセージフォローや
                続編講座の追加が効果的です。クーポン機能でリピート購入を促しましょう。
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
